import React, { useRef, useState } from 'react';
import type { ACF } from 'types';

import Image from 'next/image';

import { Button, Wrap } from '@leafwell/components';

import Figure from './figure';
import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';
import OnMediaForm from './on-media-form';

const OnMediaContent: React.FC<{ onMedia: ACF['onMedia'] }> = ({
  onMedia: { title, description, button, mediaLinks },
}) => {
  const formRef = useRef<HTMLSpanElement>();
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  const showForm = () => {
    formRef?.current?.classList?.remove('hidden', 'translate-y-full');
    formRef?.current?.classList?.add('translate-y-0', 'xl:translate-y-32');
    setIsFormOpen(true);
  };

  function hideForm(): void {
    formRef?.current?.classList?.remove('translate-y-0', 'xl:translate-y-32');
    formRef?.current?.classList?.add('hidden', 'translate-y-full');
    setIsFormOpen(false);
  }

  return (
    <>
      <Wrap tagName="header" spacer={true}>
        <div className="text-center grid gap-y-8 md:max-w-5xl justify-self-center justify-items-center">
          <HeadingBody
            {...{
              level: '1',
              tagName: 'h1',
              title: title,
              className: isFormOpen
                ? 'opacity-45 font-semibold'
                : 'opacity-100 font-semibold',
            }}
          />
          <WysiwygContent content={description} className="md:max-w-xl" />
          {button && (
            <Button
              label={button.title}
              rounded={true}
              filled={true}
              variant="secondary"
              className="w-full md:w-fit h-fit"
              onClick={() => showForm()}
            />
          )}
          <span
            ref={formRef}
            className="absolute hidden transition ease-in-out duration-500 left-0 right-0 top-2 xl:top-0 z-10 xl:z-20 grid content-center justify-center"
          >
            <OnMediaForm
              showCloseButton={true}
              onCloseButton={() => hideForm()}
            />
          </span>
        </div>
      </Wrap>
      <Wrap>
        {mediaLinks?.length > 0 ? (
          <ul
            className={[
              isFormOpen ? 'opacity-45' : 'opacity-100',
              'grid md:grid-cols-2 lg:grid-cols-3 gap-10',
            ].join(' ')}
          >
            {mediaLinks.map(
              (
                { link, logo, articleTitle, coverImage, publicationDate },
                idx,
              ) => {
                return (
                  <li key={`on-media-link-${idx}`} className="flex flex-col">
                    {coverImage ? (
                      <Figure
                        {...coverImage}
                        className="h-60 rounded-lg"
                        objectFit="cover"
                      />
                    ) : (
                      <Image
                        src="/share.jpg"
                        alt=""
                        className="h-60 border border-lilac-500 rounded-lg"
                        loading="lazy"
                        width={956}
                        height={243}
                        objectFit="cover"
                      />
                    )}
                    <Figure {...logo} className="w-40 h-20 grid items-center" />
                    <h4 className="text-xl md:text-2xl">
                      <a
                        href={link}
                        target="_blank"
                        className="relative underline-offset-small cursor-pointer focus:outline-none outline-none no-underline hover:underline hover:text-lilac-500 underline-thickness-medium select-none no-drag leading-normal"
                      >
                        {articleTitle}
                      </a>
                    </h4>
                    <p className="mt-5 text-gray-500">{publicationDate}</p>
                  </li>
                );
              },
            )}
          </ul>
        ) : null}
      </Wrap>
    </>
  );
};

export default OnMediaContent;
